/**
 * Una sola ventana por consulta.
 *
 * LiveKit no admite dos conexiones con la misma identidad: cuando llega la segunda, EXPULSA a
 * la primera (`DUPLICATE_IDENTITY`). El 04-09-2026 eso ocurrió con un médico —una segunda
 * ventana entró a la misma consulta—, la sesión que quedó viva no publicó cámara ni micrófono
 * y el paciente estuvo tres minutos con la pantalla muda. El servidor hizo lo correcto; lo que
 * faltaba era que el portal no abriera esa segunda conexión.
 *
 * Esto se resuelve entre pestañas del mismo navegador con un `BroadcastChannel`: la primera
 * ventana que abre la consulta se queda con ella, y la segunda no conecta y ofrece traerse el
 * control. Es un candado cooperativo, no una garantía: dos navegadores distintos o dos equipos
 * no se ven entre sí. Para ese caso queda el aviso de identidad duplicada en la pantalla de
 * video, que al menos explica lo que pasó en vez de dejar una llamada muda.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type Mensaje =
  /** «Estoy abriendo esta consulta»: quien la tenga debe responder. */
  | { t: 'hola'; de: string }
  /** «La tengo yo». */
  | { t: 'ocupado'; de: string }
  /** «Cédemela»: quien la tenga debe soltarla. */
  | { t: 'cedeme'; de: string };

/** Cuánto se espera una respuesta antes de darse por dueño. Un ida y vuelta local es inmediato. */
const ESPERA_RESPUESTA_MS = 400;

export interface SalaExclusiva {
  /** Otra ventana tiene la consulta: no hay que conectar. `null` mientras se averigua. */
  bloqueado: boolean | null;
  /** Traerse el control a esta ventana; la otra lo suelta. */
  tomarControl: () => void;
}

export function useSalaExclusiva(consultaId: string | undefined): SalaExclusiva {
  const [bloqueado, setBloqueado] = useState<boolean | null>(null);
  const canalRef = useRef<BroadcastChannel | null>(null);
  const yoRef = useRef<string>('');
  const dueñoRef = useRef(false);

  useEffect(() => {
    if (!consultaId) {
      setBloqueado(false);
      return;
    }
    // Sin BroadcastChannel (navegador viejo) no se bloquea nada: mejor una llamada con riesgo
    // de duplicado que una pantalla que no conecta por una capacidad que falta.
    if (typeof BroadcastChannel === 'undefined') {
      setBloqueado(false);
      return;
    }

    const yo = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    yoRef.current = yo;
    const canal = new BroadcastChannel(`teleconsulta-${consultaId}`);
    canalRef.current = canal;
    let vivo = true;

    canal.onmessage = (ev: MessageEvent<Mensaje>) => {
      const m = ev.data;
      if (!m || m.de === yo) return;
      if (m.t === 'hola' && dueñoRef.current) {
        canal.postMessage({ t: 'ocupado', de: yo } satisfies Mensaje);
      } else if (m.t === 'ocupado') {
        if (!vivo) return;
        dueñoRef.current = false;
        setBloqueado(true);
      } else if (m.t === 'cedeme') {
        // Otra ventana pidió el control: se suelta sin pelear. Su conexión desplazaría a la
        // nuestra de todos modos; soltar primero evita el rebote de expulsiones mutuas.
        dueñoRef.current = false;
        if (vivo) setBloqueado(true);
      }
    };

    canal.postMessage({ t: 'hola', de: yo } satisfies Mensaje);
    const t = setTimeout(() => {
      if (!vivo) return;
      setBloqueado((previo) => {
        if (previo === true) return previo;  // alguien respondió: no somos dueños
        dueñoRef.current = true;
        return false;
      });
    }, ESPERA_RESPUESTA_MS);

    return () => {
      vivo = false;
      clearTimeout(t);
      dueñoRef.current = false;
      canal.onmessage = null;
      canal.close();
      canalRef.current = null;
    };
  }, [consultaId]);

  const tomarControl = useCallback(() => {
    canalRef.current?.postMessage({ t: 'cedeme', de: yoRef.current } satisfies Mensaje);
    // Un respiro para que la otra ventana se desconecte antes de que esta entre; si no, el
    // servidor expulsaría a la que se está yendo y el reemplazo llegaría a medias.
    setTimeout(() => {
      dueñoRef.current = true;
      setBloqueado(false);
    }, 500);
  }, []);

  return { bloqueado, tomarControl };
}
